import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getMfeHealth } from '@/utils/appVersion/getMfeHealth';
import { createConsoleLogger } from '@/utils/devtools/console-logger';

const { log } = createConsoleLogger('BuildInfo');

export const remoteBuildInfoCacheKey = 'remoteMfeBuildInfo';

interface RemoteMfeHealth {
    mfeName?: string;
    mfeVersion?: string;
    appVersion?: string;
    buildNumber?: string;
    buildTimestamp?: string;
}

/** Same info line as the DevTools drawer's per-MFE App Version breakdown. */
const formatBuildInfo = (info: Omit<RemoteMfeHealth, 'mfeName'>): string => {
    const parts = [`v${info.mfeVersion ?? 'unknown'}`, `app ${info.appVersion ?? 'unknown'}`];
    if (info.buildNumber) {
        parts.push(`build #${info.buildNumber}`);
    }
    if (info.buildTimestamp) {
        parts.push(info.buildTimestamp);
    }
    return parts.join(' · ');
};

const fetchRemoteMfeHealth = async (remoteMfe: string): Promise<RemoteMfeHealth> => {
    const response = await fetch(`/api/tn/${remoteMfe}/health/`);

    if (!response.ok) {
        throw new Error(`Failed to fetch health for ${remoteMfe}`);
    }

    return response.json();
};

interface BuildInfoProps {
    /**
     * The MFE serving the current page when rendered by a host shell. The
     * env-based build number is inlined at build time, so on federated pages it
     * reflects the host bundle — the remote's number must be fetched at runtime.
     */
    remoteMfe?: string;
}

export default function BuildInfo({ remoteMfe }: BuildInfoProps) {
    const hostBuildNumber = process.env.NEXT_PUBLIC_BUILD_NUMBER;

    const { data: remoteHealth } = useQuery({
        queryKey: [remoteBuildInfoCacheKey, remoteMfe],
        queryFn: () => fetchRemoteMfeHealth(remoteMfe as string),
        enabled: !!remoteMfe,
        staleTime: Infinity,
        retry: false,
    });

    useEffect(() => {
        log(`host: ${formatBuildInfo(getMfeHealth())}`);
    }, []);

    useEffect(() => {
        if (remoteHealth?.buildNumber) {
            log(`${remoteHealth.mfeName ?? remoteMfe}: ${formatBuildInfo(remoteHealth)}`);
        }
    }, [remoteHealth, remoteMfe]);

    return (
        <div style={{ display: 'none' }}>
            host#{hostBuildNumber}
            {remoteHealth?.buildNumber
                ? ` ${remoteHealth.mfeName ?? remoteMfe}#${remoteHealth.buildNumber}`
                : ''}
        </div>
    );
}
